import memoize from 'memoize-one';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useStandardApiRequest } from 'my-core/hooks';
import { canViewFullOnlineCourse } from 'my-core/online-course-utils';
import { useCurrentUserProductSubscription, useCurrentUserSubscription } from 'my-core/subscription-utils';

import { createOCActivityTokenPurchase } from 'my-actions/TokenPurchaseActions';

export function useOnlineCourseContext(currentUser, onlineCourse, course) {
  const onlineCourseUser = useSelector(state =>
    getOnlineCourseUser(
      onlineCourse?.current_user_online_course_user_id,
      onlineCourse?.id,
      currentUser.id,
      state.onlineCourseUsers.items,
    ),
  );

  const userCourse = useSelector(state =>
    getUserCourse(
      onlineCourse?.current_user_user_course_id,
      onlineCourse?.course_id,
      currentUser.id,
      state.userCourses.items,
    ),
  );

  const subscription = useCurrentUserSubscription();
  const courseSubscription = useCurrentUserProductSubscription(course?.stripe_product_id);

  const canViewAll = useMemo(
    () =>
      !!onlineCourse &&
      canViewFullOnlineCourse(onlineCourse, onlineCourseUser, subscription, userCourse, courseSubscription),
    [onlineCourse, onlineCourseUser, subscription, userCourse, courseSubscription],
  );

  return {
    canViewAll,
    subscription,
    courseSubscription,
    onlineCourseUser,
    userCourse,
  };
}

const getOnlineCourseUser = memoize(
  (ocuId, ocId, userId, ocUsers) =>
    ocUsers[ocuId] || Object.values(ocUsers).find(ocu => ocu.online_course_id === ocId && ocu.user_id === userId),
);
const getUserCourse = memoize(
  (ucId, courseId, userId, userCourses) =>
    userCourses[ucId] ||
    (courseId && userId && Object.values(userCourses).find(uc => uc.course_id === courseId && uc.user_id === userId)),
);

export function useOnlineCourseTokens(userId, onlineCourseId) {
  // ONLINE COURSE TOKEN PURCHASES
  const tokenPurchases = useSelector(state => getTokenPurchases(onlineCourseId, userId, state.tokenPurchases.items));
  const { performRequest: performTokenPurchase, requestStatus: tokenPurchaseStatus } = useStandardApiRequest({
    actionCreator: createOCActivityTokenPurchase,
    errorMessage: true,
    successMessage: 'Free activity redeemed',
  });
  const purchaseWithToken = useCallback(ocaId => performTokenPurchase(ocaId), [performTokenPurchase]);

  return {
    tokenPurchases,
    tokenPurchaseStatus,
    purchaseWithToken,
  };
}

const getTokenPurchases = memoize((ocId, userId, tokenPurchases) =>
  !ocId ? [] : Object.values(tokenPurchases).filter(tp => tp.user_id === userId && tp.online_course_id === ocId),
);
